import { createMachine, assign } from "xstate";

import isPersonalEmail from "@brexhq/next/src/utils/isPersonalEmail";
import { LeadFormStatus } from "@brexhq/next/types/brex-api";

import { isVBOrPIMMSalesSegment, employeeGreaterThan50 } from "./helpers";
import {
  leadFormMachineContext,
  leadFormMachineEvent,
  leadFormMachineState,
  LeadFormSteps,
  leadFormEvents,
} from "./types";

export const gtmMachine = createMachine<
  leadFormMachineContext,
  leadFormMachineEvent,
  leadFormMachineState
>(
  {
    id: "gtmLeadForm",
    initial: LeadFormSteps.gtmEmail,
    context: {} as leadFormMachineContext,
    states: {
      [LeadFormSteps.gtmEmail]: {
        id: LeadFormSteps.gtmEmail,
        on: {
          [leadFormEvents.submit]: [
            {
              target: LeadFormSteps.gtmWebsite,
              actions: ["updateFields", "clearError"],
              cond: (_, event) => isPersonalEmail(event.value.workEmail || ""),
            },
            {
              target: LeadFormSteps.createLead,
              actions: ["updateFields", "clearError"],
            },
          ],
        },
      },
      [LeadFormSteps.gtmWebsite]: {
        id: LeadFormSteps.gtmWebsite,
        on: {
          [leadFormEvents.submit]: [
            {
              target: LeadFormSteps.createLead,
              actions: ["updateFields"],
            },
          ],
          [leadFormEvents.back]: [{ target: LeadFormSteps.gtmEmail }],
        },
      },
      [LeadFormSteps.createLead]: {
        invoke: {
          src: "createLeadMutation",
          onDone: [
            {
              target: LeadFormSteps.gtmSuccess,
              cond: (_, event) => {
                return (
                  !!event.data.salesSegment &&
                  !isVBOrPIMMSalesSegment(event.data.salesSegment)
                );
              },
              actions: assign({
                leadToken: (_, event) => event.data.leadToken,
                salesSegment: (_, event) => event.data.salesSegment,
                id: (_, event) => event.data.id,
              }),
            },
            {
              target: LeadFormSteps.gtmVBPIMM,
              cond: (_, event) => {
                return (
                  !!event.data.salesSegment &&
                  isVBOrPIMMSalesSegment(event.data.salesSegment)
                );
              },
              actions: assign({
                leadToken: (_, event) => event.data.leadToken,
                salesSegment: (_, event) => event.data.salesSegment,
                id: (_, event) => event.data.id,
              }),
            },
            {
              target: LeadFormSteps.gtmNon3PStepOne,
              actions: assign({
                leadToken: (_, event) => event.data.leadToken,
                salesSegment: (_, event) => event.data.salesSegment,
                id: (_, event) => event.data.id,
              }),
            },
          ],
          onError: {
            target: LeadFormSteps.error,
          },
        },
      },
      [LeadFormSteps.updateLead]: {
        invoke: {
          src: "updateLeadMutation",
          onDone: [
            {
              target: LeadFormSteps.gtmSuccess,
            },
          ],
          onError: LeadFormSteps.error,
        },
      },
      [LeadFormSteps.createExistingLead]: {
        invoke: {
          src: "createLeadMutation",
          onDone: [
            {
              target: LeadFormSteps.gtmSuccess,
            },
          ],
          onError: LeadFormSteps.error,
        },
      },
      [LeadFormSteps.gtmNon3PStepOne]: {
        id: LeadFormSteps.gtmNon3PStepOne,
        on: {
          [leadFormEvents.submit]: [
            {
              target: LeadFormSteps.gtmNon3PStepTwo,
              cond: (_, event) => {
                return !employeeGreaterThan50(event.value?.employeeCount);
              },
              actions: ["updateFields"],
            },
            {
              target: LeadFormSteps.updateLead,
              actions: [
                "updateFields",
                assign({
                  status: () => LeadFormStatus.COMPLETED,
                }),
              ],
            },
          ],
          [leadFormEvents.back]: [{ target: LeadFormSteps.gtmEmail }],
        },
      },
      [LeadFormSteps.gtmNon3PStepTwo]: {
        id: LeadFormSteps.gtmNon3PStepTwo,
        on: {
          [leadFormEvents.submit]: [
            {
              target: LeadFormSteps.updateLead,
              actions: [
                "updateFields",
                assign({
                  status: () => LeadFormStatus.COMPLETED,
                }),
              ],
            },
          ],
          [leadFormEvents.back]: [{ target: LeadFormSteps.gtmNon3PStepOne }],
        },
      },
      [LeadFormSteps.gtmVBPIMM]: {
        id: LeadFormSteps.gtmVBPIMM,
        on: {
          [leadFormEvents.submit]: [
            {
              target: LeadFormSteps.updateLead,
              actions: [
                "updateFields",
                assign({
                  status: () => LeadFormStatus.COMPLETED,
                }),
              ],
            },
          ],
          [leadFormEvents.back]: [{ target: LeadFormSteps.gtmEmail }],
        },
      },
      [LeadFormSteps.gtmExistingLead]: {
        id: LeadFormSteps.gtmExistingLead,
        on: {
          [leadFormEvents.submit]: [
            {
              target: LeadFormSteps.createExistingLead,
              actions: [
                "updateFields",
                assign({
                  status: () => LeadFormStatus.COMPLETED,
                }),
              ],
            },
          ],
          [leadFormEvents.back]: [{ target: LeadFormSteps.gtmEmail }],
        },
      },
      [LeadFormSteps.gtmSuccess]: {
        id: LeadFormSteps.gtmSuccess,
        type: "final",
      },
      [LeadFormSteps.error]: {},
    },
    on: {
      existingLead: { target: LeadFormSteps.gtmExistingLead },
    },
  },
  {
    actions: {
      updateFields: assign((_, event) => {
        if (event.type === leadFormEvents.submit) {
          return { ...event.value };
        }
        return {};
      }),
      clearContext: assign({
        salesSegment: undefined,
        leadToken: undefined,
        id: undefined,
        status: undefined,
        employeeCount: undefined,
        isEcomm: undefined,
        fundingSource: undefined,
        monthlySales: undefined,
      }),
    },
  },
);
